import { Box, Grid, Typography } from '@mui/material';
import { Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import {
  Applicant,
  nonIndividualMdmsQuestionsFatca,
  tinValidationProps,
  tinCountriesList,
  FatcaMdms,
} from '../../redux-store/types/api-types';
import { mdmsCountriesList } from '../../redux-store/types/mdms';
import {
  AssignAnsAndVisibilityForInitialFatca,
  BorderedTextField,
  Group,
  OpenTextField,
  SectionHeader,
  SingleChoiceRadio,
} from '../NonIndividualInvestor/fatca';
import { styles } from '../../Styles/styles';
import { useFeatureToggles, useMdmsCountryData, useMdmsFatcaData } from '../../utils/useDataMdms';
import { KeyValue } from '../ApplicationDetails/contributor';
import { RootStateType } from '../../redux-store/reducers';
import { getTinCountriesList } from '../../redux-store/actions/onBoarding';
import { useDispatch, useSelector } from 'react-redux';
import { FatcaMdmsData } from '../../redux-store/actions/application';

interface Values {
  applicants: {
    fatcadetail: nonIndividualMdmsQuestionsFatca;
    typeOfAddressProvidedAtKRA: string;
    tinValidation: tinValidationProps[] | null;
    // documents: doc[];
  }[];
}

export default function Fatca({
  applicants = [],
}: {
  applicants: Partial<Applicant>[];
}): JSX.Element {
  const { application } = useSelector((store: RootStateType) => store.application);
  const [fatcaData, setFatcaData] = useState<Values>();
  const [mdmsCountriesList, setMdmsCountriesList] = useState<mdmsCountriesList[]>([]);
  const [tinCountries, setTinCountries] = useState<tinCountriesList[]>([]);
  const { countries } = useMdmsCountryData();
  const fatcaName = useMdmsFatcaData();
  const dispatch = useDispatch();

  const { toggler } = useFeatureToggles();
  useEffect(() => {
    (async function () {
      try {
        const res = (await dispatch(getTinCountriesList())) as unknown as tinCountriesList[];
        setTinCountries(res);
        setMdmsCountriesList(countries.countries);
        if (fatcaName && application) {
          const response = (await dispatch(FatcaMdmsData(fatcaName))) as unknown as FatcaMdms;
          if (response.fatca_questions) {
            const _applicants = {
              applicants: applicants.map((applicant) => {
                const {
                  fatcadetail: existingFatcaDetails = null,
                  documents,
                  isTinVerified = [],
                } = applicant || {};
                return {
                  fatcadetail: existingFatcaDetails
                    ? AssignAnsAndVisibilityForInitialFatca(
                        response.fatca_questions,
                        existingFatcaDetails
                      )
                    : [],
                  typeOfAddressProvidedAtKRA: applicant.typeOfAddressProvidedAtKRA,
                  tinValidation: isTinVerified.map((item) => ({ isTinVerified: item })),
                  documents: documents,
                };
              }),
            };
            setFatcaData(_applicants as Values);
          }
        }
      } catch (e) {
        console.error((e as Error).message);
      }
    })();
  }, [countries, fatcaName]);
  return (
    <Box sx={styles.ApplicationDetailsCommonLayout} mt={5}>
      <Box sx={{ p: 2 }}>
        <Grid container sx={{ mt: 1, pl: { xs: 2, sm: 10 }, pr: { xs: 2, sm: 10 } }}>
          <Typography sx={{ fontSize: 18, fontWeight: 600, color: 'primary.main', mb: 3 }}>
            Fatca Details
          </Typography>
          <Formik
            initialValues={fatcaData as Values}
            enableReinitialize={true}
            onSubmit={() => {
              console.log('this action should not be trigered');
            }}>
            {({ values }) => (
              <Grid
                container
                rowSpacing={1}
                //columnSpacing={5}
                sx={{
                  width: '100%',
                  ml: 0,
                  '.MuiGrid-item': { px: { xs: 0, sm: '30px' } },
                }}>
                {values?.applicants.map((applicant, idx) => (
                  <React.Fragment key={idx}>
                    {applicant.fatcadetail.length === 0 && (
                      <Grid
                        xs={12}
                        sm={12}
                        sx={{
                          fontSize: 18,
                          fontWeight: 600,
                          color: 'common.black',
                          textAlign: 'center',
                          my: 3,
                        }}>
                        No Fatca Details
                      </Grid>
                    )}
                    <KeyValue
                      title={'Type Of Address Provided At KRA'}
                      description={applicant.typeOfAddressProvidedAtKRA}
                      fullWidth
                    />

                    {applicant.fatcadetail.map((item) => (
                      <React.Fragment key={item.id}>
                        {item.isVisible &&
                          item.section_header &&
                          item.question_type !== 'group' && (
                            <SectionHeader value={item.section_header} />
                          )}
                        {item.isVisible && item.question_type === 'single_choice_radio' && (
                          <SingleChoiceRadio
                            data={item}
                            values={applicant.fatcadetail}
                            formValuesIndex={idx}
                            questionId={item.id}
                            disabled={true}
                          />
                        )}
                        {item.isVisible && item.question_type === 'group' && (
                          <Group
                            data={item}
                            formValuesIndex={idx}
                            values={applicant.fatcadetail}
                            _applicants={applicants}
                            disabled={true}
                            nationalityDropdown={mdmsCountriesList}
                            setValue={() => {
                              console.log('clicked');
                            }}
                            createdAt={application?.createdAt || ''}
                            tinVerification={applicant.tinValidation || []}
                            documents={[]}
                            tinCountries={tinCountries}
                            // formVal={fatcaData}
                            toggler={toggler}
                          />
                        )}
                        {item.isVisible && item.question_type === 'open_text_field' && (
                          <OpenTextField
                            text={item.question_text}
                            applicantsIndex={idx}
                            questionId={item.id}
                            placeHolder={item?.placeholder || 'Please enter text here'}
                            isMandatory={item.isMandatory}
                            disabled={true}
                          />
                        )}
                        {item.isVisible && item.question_type === 'bordered_text_field' && (
                          <Grid item xs={12} sm={6} sx={{ pr: { xs: 0, sm: '30px' }, pt: 2 }}>
                            <BorderedTextField
                              applicantsIndex={idx}
                              questionId={item.id}
                              label={item.question_text}
                              placeHolder={item?.placeholder || 'Please enter text here'}
                              isMandatory={item.isMandatory}
                              disabled={true}
                            />
                          </Grid>
                        )}
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                ))}
              </Grid>
            )}
          </Formik>
        </Grid>
      </Box>
    </Box>
  );
}
